import { AfterViewChecked, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { LoggingService } from '../../utils/logging.service';
import { RANGE_SLIDER_MAX_VALUE, RANGE_SLIDER_MIN_VALUE, RangeSliderButtonType, RangeSliderSlideValue, RangeSliderValue } from './range-slider';
import { RangeSliderUtils } from './range-slider.utils';


const FORM_INPUT_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RangeSliderComponent),
    multi: true
};

/**
 * PRIVATE!
 * Implementierungen sind {@link FormRangeDateComponent} und {@link FormRangeNumberComponent}.
 *
 * Diese Komponente stellt einen Schieberegler dart und ein ControlValueAccessor benutzt.
 * Datenmodelle: {from, to}, wo "from" (auch wie "to") ist ein Float von 0 bis 100
 */
@Component({
    selector: 'common-range-slider',
    template: `
        <div class="rangeSlider">
            <div class="rangeSliderVisualLine"></div>
            <div class="rangeSliderControl" #controlElement>
                <div class="rangeSliderControlButtons" [style.left]="cssControlLeft" [style.width]="cssControlWidth">
                    <div class="rangeSliderControlVisualLine"></div>
                    <a href="javascript:" class="icon-slider rangeSliderControlButtonL"
                       rangeSliderButton
                       tabindex="0"
                       (changeBegin)="onButtonChangeBegin('from', $event)"
                       (touch)="onButtonTouch()"></a>
                    <a href="javascript:" class="icon-slider rangeSliderControlButtonR"
                       rangeSliderButton
                       tabindex="0"
                       (changeBegin)="onButtonChangeBegin('to', $event)"
                       (touch)="onButtonTouch()"></a>
                </div>
            </div>
        </div>
    `,
    providers: [FORM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class RangeSliderComponent implements OnInit, OnDestroy, AfterViewChecked, ControlValueAccessor {

    @ViewChild('controlElement') controlElement;

    @Output() sliderMove: EventEmitter<RangeSliderValue> = new EventEmitter();

    disabled: boolean = false;

    cssControlLeft: string = '0';

    cssControlWidth: string = '100%';

    controlRect: ClientRect;

    value: RangeSliderValue = { from: RANGE_SLIDER_MIN_VALUE, to: RANGE_SLIDER_MAX_VALUE };

    private change: EventEmitter<RangeSliderValue> = new EventEmitter<RangeSliderValue>();

    private touch: EventEmitter<RangeSliderValue> = new EventEmitter<RangeSliderValue>();

    constructor(private utils: RangeSliderUtils, private logger: LoggingService) {
    }

    ngOnInit(): void {
        this.updateCssControls();
    }

    ngAfterViewChecked(): void {
        this.controlRect = this.controlElement.nativeElement.getBoundingClientRect();
    }

    ngOnDestroy(): void {
        this.change.complete();
        this.touch.complete();
    }

    writeValue(value: RangeSliderValue): void {
        if (value === null || value === undefined || typeof value !== 'object' ||
            !value.hasOwnProperty('from') || !value.hasOwnProperty('to')) {
            this.value = { from: RANGE_SLIDER_MIN_VALUE, to: RANGE_SLIDER_MAX_VALUE };
            this.updateCssControls();
            return;
        }
        let valueFrom = value.from < RANGE_SLIDER_MIN_VALUE ? RANGE_SLIDER_MIN_VALUE : value.from;
        let valueTo = value.to > RANGE_SLIDER_MAX_VALUE ? RANGE_SLIDER_MAX_VALUE : value.to;

        if (valueFrom > valueTo) {
            valueFrom = valueTo;
        }
        if (valueTo < valueFrom) {
            valueTo = valueFrom;
        }

        this.value = { from: valueFrom, to: valueTo };
        this.updateCssControls();
    }

    registerOnChange(fn: any): void {
        this.change.subscribe(fn);
    }

    registerOnTouched(fn: any): void {
        this.touch.subscribe(fn);
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled
    }

    /**
     * Jede mousedown event stellt als neues onButtonChangeBegin Event dart.
     * Das wird so implementiert um Initiale Werte zu speichern, z.B. initialValue und oppositeValue
     */
    onButtonChangeBegin(buttonType: RangeSliderButtonType, changes: Observable<RangeSliderSlideValue>) {
        if (!this.disabled) {
            const initialValue = this.value[buttonType];
            const oppositeValue = this.value[buttonType === 'from' ? 'to' : 'from'];

            changes.subscribe({
                next: (x) => {
                    const value = this.utils.positionToValue(buttonType, x, this.controlRect, oppositeValue, initialValue);
                    this.value = Object.assign({}, this.value); // immutable
                    this.value[buttonType] = value;
                    this.writeValue(this.value);
                    this.sliderMove.emit(this.value);
                },
                error: (err) => this.logger.error(err),
                complete: () => this.change.emit(this.value)
            });
        }
    }

    onButtonTouch() {
        this.touch.emit(this.value);
    }

    private updateCssControls() {
        this.cssControlLeft = this.value.from + '%';
        this.cssControlWidth = this.value.to - this.value.from + '%';
    }
}
